import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  LOCALE_HTML_LANG,
  getSiteStrings,
  getStoredLocale,
  setStoredLocale,
  type LocaleId,
  type SiteStrings,
} from "@dawn/i18n";

/** Site copy, `document.lang`, and localStorage. HUD game copy lives in `HudLocaleContext`. */
export interface LocaleContextValue {
  locale: LocaleId;
  strings: SiteStrings;
  setLocale: (locale: LocaleId) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function applyDocumentLocale(locale: LocaleId): void {
  document.documentElement.lang = LOCALE_HTML_LANG[locale];
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleId>(() => getStoredLocale());

  useLayoutEffect(() => {
    applyDocumentLocale(locale);
  }, [locale]);

  const setLocale = useCallback((next: LocaleId): void => {
    setStoredLocale(next);
    setLocaleState(next);
  }, []);

  const value = useMemo(
    (): LocaleContextValue => ({
      locale,
      strings: getSiteStrings(locale),
      setLocale,
    }),
    [locale, setLocale],
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return value;
}
